import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { type TranslationFn, useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type RoleFormSchema, roleSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { RoleForm } from "@/forms/role-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface DuplicateRoleModalProps extends ModalProps {
	roleId: string;
	onComplete?: () => Promise<void>;
}

/**
 * Build the suggested name for a copy so that it already validates.
 *
 * `roleSchema` caps a name at 64 characters, and the suffix costs a few more — so a source name
 * near the cap would open the dialog on a field the user has not touched and that is already
 * too long. The source is shortened by exactly the overflow instead, which keeps the beginning
 * of the name (the part that identifies it) and reads the cap off the schema rather than
 * restating it. The suffix length is read from the rendered string because it differs per
 * language: ` (copy)` is seven characters, ` (Kopie)` is eight.
 *
 * @param name - The source role's name, as the server currently has it.
 * @param t - The translation function, used to render `roles.copy_suffix`.
 * @returns A suffixed name no longer than the schema allows.
 */
function buildCopyName(name: string, t: TranslationFn): string {
	const maxLength = roleSchema.shape.name.maxLength ?? Number.POSITIVE_INFINITY;
	const suffixed = t("roles.copy_suffix", { name });
	if (suffixed.length <= maxLength) return suffixed;
	const truncated = name.slice(0, name.length - (suffixed.length - maxLength)).trimEnd();
	return t("roles.copy_suffix", { name: truncated });
}

export function DuplicateRoleModal({
	isOpen,
	onClose,
	roleId,
	onComplete,
}: DuplicateRoleModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	// The source is fetched rather than passed in, so the suggested name is built from the
	// role as it is right now — a rename that happened in another tab is not copied under
	// the old name.
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.roles[":roleId"].$get({
			param: { roleId },
		});
		const data = await responseHandler(response);
		return { name: buildCopyName(data.role.name, t) };
	});
	const methods = useForm<RoleFormSchema>({
		resolver: resolver(roleSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: RoleFormSchema) {
		const response = await client.roles[":roleId"].duplicate.$post({
			param: { roleId },
			json,
		});
		await responseHandler(response);
		// The copy is a new role that every list naming roles has to learn about, and the caller
		// may be about to grant it to themselves — so the resolved abilities go too.
		await qc.invalidateQueries({
			predicate: invalidate(["roles", "citadel", "user-permissions-form"]),
		});
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("roles.duplicate_role")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit, console.error)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<RoleForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
